import { DecimalPipe, NgFor, NgIf, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy, ViewChild, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { fuseAnimations } from '@fuse/animations/public-api';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { ChartComponent } from "ng-apexcharts";
import { DataVisualBarService } from './dv-bar.service';
import { ApexNonAxisChartSeries, ApexResponsive, ApexChart, ApexStroke, ApexFill } from "ng-apexcharts";
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { DateTime } from 'luxon';
import { StatsService } from 'app/services/stats.service';
import { FileCount } from 'app/models/stats';
import { Util } from 'app/models/util';

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  stroke: ApexStroke;
  fill: ApexFill;
};

@Component({
    selector     : 'dv-bar',
    templateUrl  : './dv-bar.component.html',
    styleUrls      : ['./dv-bar.component.scss'],
    animations: [
        fuseAnimations,
        trigger('slideDown', [
          state('void', style({
            height: '0',
            opacity: '0',
            overflow: 'hidden',
          })),
          state('*', style({
            height: '*',
            opacity: '1',
          })),
          transition(':leave', [
            animate('0.3s ease-out')
          ]),
        ])
      ],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone   : true,
    imports      : [
        MatButtonModule, 
        MatIconModule, 
        MatTooltipModule,
        FormsModule, 
        ReactiveFormsModule, 
        MatFormFieldModule, 
        MatInputModule, 
        MatExpansionModule,
        MatProgressBarModule,
        MatDividerModule,
        CommonModule,
        NgFor,
        NgIf,
        FuseScrollbarDirective,
        MatMenuModule, 
        MatButtonToggleModule, 
        NgApexchartsModule, 
        MatTooltipModule, 
        NgFor, 
        DecimalPipe,
    ],
})

export class DataVisualBarComponent implements OnInit, OnChanges {

    @Input() currentFolderId:string=null
    chartFileCount: ApexOptions = {};
    chartFileDistribution: ApexOptions = {};

    data: any;
    workaroundData: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    now = DateTime.now();
    public params: string = 'current';

    private _themeColors: Record<string, Record<string, string>> = {
        default: {
            Documents:     '#4F8EF7',
            PDFs:          '#EF4444',
            Spreadsheets:  '#22C55E',
            Images:        '#A855F7',
            Presentations: '#F97316',
            Audios:        '#06B6D4',
            Videos:        '#6366F1',
            Archives:      '#78716C',
        },
        panther_default: {
            Documents:     '#162FEF',
            PDFs:          '#E53935',
            Spreadsheets:  '#43A047',
            Images:        '#5C6DF4',
            Presentations: '#FB8C00',
            Audios:        '#00ACC1',
            Videos:        '#3949AB',
            Archives:      '#6D4C41',
        },
        panther_rose: {
            Documents:     '#F43F5E',
            PDFs:          '#E11D48',
            Spreadsheets:  '#22C55E',
            Images:        '#A855F7',
            Presentations: '#F97316',
            Audios:        '#06B6D4',
            Videos:        '#6366F1',
            Archives:      '#78716C',
        },
    };

    private get _categoryColors(): Record<string, string> {
        const client_theme_name = this._util.WebComponentInputParams.clientTheme.name;

        const name = client_theme_name ?? 'default';
        return this._themeColors[name] ?? this._themeColors['default'];
    }

    fileCount={
        series:[],
        labels:[],
        colors:[]
    }

    recentModifiedCount={
        series:[],
        labels:[],
        colors:[]
    }

    /**
     * Constructor
     */
    constructor(
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        private _dvBarService: DataVisualBarService,
        private _statsService: StatsService,
        private _util: Util,
    ){}

    ngOnChanges(changes: SimpleChanges): void {
        
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * Initializes the component.
     * Subscribes to the data service to fetch data and prepares chart data.
     */
    ngOnInit(): void {

        this.getFileCounts()
        this.getRecentModifiedCounts()

        // Attach SVG fill fixer to all ApexCharts
        window['Apex'] = {
            chart: {
                events: {
                    mounted: (chart: any, options?: any): void =>
                    {
                        this._fixSvgFill(chart.el);
                    },
                    updated: (chart: any, options?: any): void =>
                    {
                        this._fixSvgFill(chart.el);
                    },
                },
            },
        };

    }
    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    getFileCounts(){
    this._statsService.getFileCount(this.currentFolderId).then((res:any)=>{
        this.setFileCountChart(res.data)
    }).catch(error=>{
        console.log(error)
    })
    }

    getRecentModifiedCounts(){
    this._statsService.getRecentModifiedFiles(this.currentFolderId).then((res:any)=>{
        this.setRecentModifiedCount(res.data)
    }).catch(error=>{
        console.log(error)
    })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Fix the SVG fill references. This fix must be applied to all ApexCharts
     * charts in order to fix 'black color on gradient fills on certain browsers'
     * issue caused by the '<base>' tag.
     *
     * Fix based on https://gist.github.com/Kamshak/c84cdc175209d1a30f711abd6a81d472
     *
     * @param element
     * @private
     */
    private _fixSvgFill(element: Element): void
    {
        // Current URL
        const currentURL = this._router.url;

        // 1. Find all elements with 'fill' attribute within the element
        // 2. Filter out the ones that doesn't have cross reference so we only left with the ones that use the 'url(#id)' syntax
        // 3. Insert the 'currentURL' at the front of the 'fill' attribute value
        Array.from(element.querySelectorAll('*[fill]'))
            .filter(el => el.getAttribute('fill').indexOf('url(') !== -1)
            .forEach((el) =>
            {
                const attrVal = el.getAttribute('fill');
                el.setAttribute('fill', `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`);
            });
    }

    /**
     * Prepare the chart data from the data
     *
     * @private
     */
    private _prepareChartFileDistribution(): void {

        //Set the chart options

        this.chartFileDistribution = {
            chart      : {
                fontFamily: 'inherit',
                foreColor : 'inherit',
                height    : '100%',
                type      : 'polarArea',
                toolbar   : {
                    show: true,
                },
                zoom      : {
                    enabled: true,
                },
            },
            labels     : this.recentModifiedCount.labels,
            legend     : {
                position: 'bottom',
            },
            plotOptions: {
                polarArea: {
                    spokes: {
                        connectorColors: 'var(--fuse-border)',
                    },
                    rings : {
                        strokeColor: 'var(--fuse-border)',
                    },
                },
            },
            colors     : this.recentModifiedCount.colors,
            series     : this.recentModifiedCount.series,
            states     : {
                hover: {
                    filter: {
                        type : 'lighten',
                        value: 0.25,
                    },
                },
            },
            stroke     : {
                width: 2,
            },
            tooltip    : {
                followCursor: true,
                theme       : 'dark',
            },
            yaxis      : {
                labels: {
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                    formatter: (value) => {
                        return value.toFixed(1)
                      },
                },
            },
        };

    }
    
    private _prepareChartFileCount(): void {
        //Set the chart options
        this.chartFileCount = {
            chart      : {
                fontFamily: 'inherit',
                foreColor : 'inherit',
                height    : '100%',
                type      : 'donut',
                toolbar   : {
                    show: true,
                },
                zoom      : {
                    enabled: true,
                },
            },
            labels     : this.fileCount.labels,
            legend     : {
                position: 'bottom',
            },
            plotOptions: {
                pie: {
                    customScale: 0.9,
                    donut: {
                        size: '62.8318%',
                        labels: {
                            show: true,
                          }
                      }
                  }
            },
            colors     : this.fileCount.colors,
            series     : this.fileCount.series,
            states     : {
                hover: {
                    filter: {
                        type : 'lighten',
                        value: 0.25,
                    },
                },
            },
            stroke     : {
                width: 2,
            },
            tooltip    : {
                followCursor: true,
                theme       : 'dark',
            },
            yaxis      : {
                labels: {
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                },
            },
            responsive: [
                {
                  breakpoint: 480,
                  options: {
                  
                    legend: {
                      position: "right"
                    }
                  }
                }
              ]
        };
    }

addNonZeroCount(label: string, count: number) {
    if (count > 0) {
        this.fileCount.labels.push(label);
        this.fileCount.series.push(count);
        this.fileCount.colors.push(this._categoryColors[label] ?? '#94A3B8');
    }
}

addNonZeroRecent(label: string, count: number) {
    if (count > 0) {
        this.recentModifiedCount.labels.push(label);
        this.recentModifiedCount.series.push(count);
        this.recentModifiedCount.colors.push(this._categoryColors[label] ?? '#94A3B8');
    }
}

setFileCountChart(data:FileCount){
    // Populate labels and series arrays
this.addNonZeroCount('Documents', data.documentCount);
this.addNonZeroCount('PDFs', data.pdfCount);
this.addNonZeroCount('Spreadsheets', data.spreadsheetCount);
this.addNonZeroCount('Images', data.imageCount);
this.addNonZeroCount('Presentations', data.presentationCount);
this.addNonZeroCount('Audios', data.audioCount);
this.addNonZeroCount('Videos', data.videoCount);
this.addNonZeroCount('Archives', data.archiveCount);
console.log("fileCount",this.fileCount)
    this._prepareChartFileCount()
}

setRecentModifiedCount(data:FileCount){
    // Populate labels and series arrays
this.addNonZeroRecent('Documents', data.documentCount);
this.addNonZeroRecent('PDFs', data.pdfCount);
this.addNonZeroRecent('Spreadsheets', data.spreadsheetCount);
this.addNonZeroRecent('Images', data.imageCount);
this.addNonZeroRecent('Presentations', data.presentationCount);
this.addNonZeroRecent('Audios', data.audioCount);
this.addNonZeroRecent('Videos', data.videoCount);
this.addNonZeroRecent('Archives', data.archiveCount);
    this._prepareChartFileDistribution()
}

}
